/* @flow */
// typecheck: ignore file
import { Map } from 'immutable';
import type { Action } from 'action';
import GroupMembership from 'entities/GroupMembership';
import type { ClassMembershipFetchOptions } from 'sagas/peopleTables';

export type PeopleTable = {|
  page: number,
  loading: boolean,
  allLoaded: boolean,
  memberships: GroupMembership[],
  selectedMemberships: Map<number, GroupMembership>,
|};

export type State = Map<number, PeopleTable>;

export const initialState: State = new Map();

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case 'peopleTables/INITIALIZE_TABLE': {
      return state.set(action.groupId, {
        page: 1,
        loading: false,
        allLoaded: false,
        memberships: [],
        selectedMemberships: new Map(),
      });
    }

    case 'peopleTables/LOAD_MEMBERSHIPS_REQUEST': {
      const table = state.get(action.groupId);
      return state.set(action.groupId, {
        ...table,
        loading: true,
      });
    }

    case 'peopleTables/LOAD_MEMBERSHIPS_SUCCESS': {
      const table = state.get(action.groupId);
      return state.set(action.groupId, {
        ...table,
        loading: false,
        page: action.options.page,
        allLoaded: action.memberships.length < action.options.perPage,
        memberships: action.options.page === 1 ? action.memberships : [].concat(table.memberships, action.memberships),
      });
    }

    case 'peopleTables/LOAD_MEMBERSHIP_SUCCESS': {
      const { groupId, membership, options } = action;

      const table = state.get(groupId);
      const update = options && options.update;

      let updated = false;

      const memberships = table.memberships.map(m => {
        if (m.id !== membership.id) {
          return m;
        }

        updated = true;
        return membership;
      });

      if (!update && !updated) {
        memberships.push(membership);
      }

      return state.set(action.groupId, { ...table, memberships });
    }

    case 'peopleTables/SELECT_MEMBERSHIP': {
      const table = state.get(action.groupId);
      return state.set(action.groupId, {
        ...table,
        selectedMemberships: table.selectedMemberships.set(action.membership.id, action.membership),
      });
    }

    case 'peopleTables/UNSELECT_MEMBERSHIP': {
      const table = state.get(action.groupId);
      return state.set(action.groupId, {
        ...table,
        selectedMemberships: table.selectedMemberships.delete(action.membership.id),
      });
    }

    case 'peopleTables/UNSELECT_ALL_MEMBERSHIP': {
      const table = state.get(action.groupId);
      return state.set(action.groupId, {
        ...table,
        selectedMemberships: table.selectedMemberships.clear(),
      });
    }

    case 'peopleTables/REMOVE_MEMBERS': {
      const ids = action.userIds;
      const table = state.get(action.groupId);
      const memberships = table.memberships.filter(m => !ids.includes(m.getUserId()));
      const selectedMemberships = table.selectedMemberships.filter(m => !ids.includes(m.getUserId()));

      return state.set(action.groupId, {
        ...table,
        memberships,
        selectedMemberships,
      });
    }

    default: {
      return state;
    }
  }
}

type InitializeTableAction = {
  type: 'peopleTables/INITIALIZE_TABLE',
  groupId: number,
};

export function initializeTable(groupId: number): InitializeTableAction {
  return {
    type: 'peopleTables/INITIALIZE_TABLE',
    groupId,
  };
}

type SelectMembershipAction = {
  type: 'peopleTables/SELECT_MEMBERSHIP',
  membership: GroupMembership,
  groupId: number,
};

export function selectMembership(groupId: number, membership: GroupMembership): SelectMembershipAction {
  return {
    type: 'peopleTables/SELECT_MEMBERSHIP',
    membership,
    groupId,
  };
}

type UnselectMembershipAction = {
  type: 'peopleTables/UNSELECT_MEMBERSHIP',
  membership: GroupMembership,
  groupId: number,
};

export function unselectMembership(groupId: number, membership: GroupMembership): UnselectMembershipAction {
  return {
    type: 'peopleTables/UNSELECT_MEMBERSHIP',
    membership,
    groupId,
  };
}

type UnselectAllMembershipAction = {
  type: 'peopleTables/UNSELECT_ALL_MEMBERSHIP',
  groupId: number,
};

export function unselectAllMembership(groupId: number): UnselectAllMembershipAction {
  return {
    type: 'peopleTables/UNSELECT_ALL_MEMBERSHIP',
    groupId,
  };
}

export type LoadMembershipsAction = {
  type: 'peopleTables/LOAD_MEMBERSHIPS_REQUEST',
  groupId: number,
  options: any,
};

export function loadMemberships(groupId: number, options: ClassMembershipFetchOptions): LoadMembershipsAction {
  return {
    type: 'peopleTables/LOAD_MEMBERSHIPS_REQUEST',
    groupId,
    options,
  };
}

export type LoadMembershipsSuccessAction = {
  type: 'peopleTables/LOAD_MEMBERSHIPS_SUCCESS',
  memberships: any,
  groupId: number,
  options: any,
};

export function loadMembershipsSuccess(groupId: number, memberships: GroupMembership[], options: ClassMembershipFetchOptions): LoadMembershipsSuccessAction {
  return {
    type: 'peopleTables/LOAD_MEMBERSHIPS_SUCCESS',
    memberships,
    groupId,
    options,
  };
}

export type RemoveMembersAction = {
  type: 'peopleTables/REMOVE_MEMBERS',
  userIds: number[],
  groupId: number,
};

export function removeMembers(groupId: number, userIds: number[]): RemoveMembersAction {
  return {
    type: 'peopleTables/REMOVE_MEMBERS',
    userIds,
    groupId,
  };
}

export type LoadMembershipOptions = {
  update?: boolean // when true, will ignore if not in store.
};

export type LoadMembershipAction = {
  type: 'peopleTables/LOAD_MEMBERSHIP_REQUEST',
  groupId: number,
  userId: number,
  options: LoadMembershipOptions,
};

export function loadMembership(groupId: number, userId: number, options: LoadMembershipOptions): LoadMembershipAction {
  return {
    type: 'peopleTables/LOAD_MEMBERSHIP_REQUEST',
    groupId,
    userId,
    options,
  };
}

export type LoadMembershipSuccessAction = {
  type: 'peopleTables/LOAD_MEMBERSHIP_SUCCESS',
  membership: GroupMembership,
  groupId: number,
  options: LoadMembershipOptions
};

export function loadMembershipSuccess(groupId: number, membership: GroupMembership, options: LoadMembershipOptions): LoadMembershipSuccessAction {
  return {
    type: 'peopleTables/LOAD_MEMBERSHIP_SUCCESS',
    membership,
    options,
    groupId,
  };
}

export type PeopleTablesAction =
  RemoveMembersAction |
  SelectMembershipAction |
  UnselectMembershipAction |
  UnselectAllMembershipAction |
  LoadMembershipsAction |
  LoadMembershipsSuccessAction |
  LoadMembershipSuccessAction |
  LoadMembershipAction;



// WEBPACK FOOTER //
// ./src/reducers/peopleTables.js